/**
 * Loading Spinner Component
 * @author Muhammad Arsan
 * @description A reusable Button component,  component using Material-UI's Button component
 */

import React from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { buttonBorderRadius, buttonRadiusClassName } from "@config/theme/tokens";
import type { ButtonProps, ButtonVariant } from "./types/button";

const variantClasses: Record<ButtonVariant, string> = {
	primary: "bg-primary text-white hover:bg-[#025c9a]",
	secondary:
		"bg-primary-surface text-primary border border-primary-surface-border hover:bg-primary-surface-hover",
	icon: "text-black hover:text-primary p-1",
	tertiary: "bg-white font-semibold text-primary border border-primary hover:bg-gray-100",
	danger: "bg-red-600 text-white hover:bg-red-600",
	dangerSecondary: "bg-red-400 text-white hover:bg-red-600 font-semibold",
    dangerTertiary: "bg-white font-semibold text-red-500 border border-red-500 hover:bg-red-100",
};

const Button: React.FC<ButtonProps> = React.memo(
	({
		text,
		variant = "primary",
		loading = false,
		isDisabled = false,
		className = "",
		disabled,
		children,
		style,
		...props
	}) => {
		const isButtonDisabled = loading || isDisabled || disabled;

		const baseClasses =
			variant === "icon"
				? "font-sans tracking-wide transition-colors duration-150 flex items-center justify-center"
				: `${buttonRadiusClassName} font-sans tracking-wide px-6 py-2 transition-colors duration-150 flex items-center justify-center gap-2`;

		const hoverClasses = variant === "icon" ? "" : "hover:shadow-md";
		const buttonStyle =
			variant === "icon" ? style : { borderRadius: buttonBorderRadius, ...style };

		return (
			<button
				type="button"
				className={`${baseClasses} ${variantClasses[variant]} ${
					isButtonDisabled ? "opacity-60 cursor-not-allowed" : hoverClasses
				} ${className}`}
				disabled={isButtonDisabled}
				style={buttonStyle}
				{...props}>
				{loading && <CircularProgress size={18} color="inherit" />}
				{children ? children : text}
			</button>
		);
	},
);

export default Button;
